import {
    Component,
    computed,
    DestroyRef,
    inject,
    Injector,
    OnInit,
    signal,
} from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { get, invalidateToken, isMock, onlineState } from '@placeos/ts-client';

import {
    ActivatedRoute,
    NavigationEnd,
    Router,
    RouterOutlet,
} from '@angular/router';
import { setupCache, updateAvailable } from './common/application';
import { log } from './common/general';
import { setNotifyOutlet } from './common/notifications';
import { PlaceSettings, setLoadingMessage, setupPlace } from './common/placeos';
import { initSentry } from './common/sentry';
import { SettingsService } from './common/settings.service';
import { signalFromClient, waitForSignalValue } from './common/signals';
import { currentUser } from './common/user-state';
import { BackofficeUsersService } from './users/users.service';

import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltip } from '@angular/material/tooltip';
import type { PlaceTenant } from './admin/staff-api.component';
import {
    localeFromUrl,
    LocaleService,
    setTranslationService,
} from './common/locale.service';
import { GlobalBannerComponent } from './ui/global-banner.component';
import { GlobalLoadingComponent } from './ui/global-loading.component';
import { IconComponent } from './ui/icon.component';
import { UploadListComponent } from './ui/upload-list.component';

/** Longest time start up waits for translations */
const LOCALE_TIMEOUT_MS = 5000;

/** Signal of the browser's network state. Call in an injection context. */
function browserOnline() {
    const state = signal(navigator.onLine);
    const update = () => state.set(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    inject(DestroyRef).onDestroy(() => {
        window.removeEventListener('online', update);
        window.removeEventListener('offline', update);
    });
    return state.asReadonly();
}

@Component({
    selector: 'placeos-root',
    template: `
        <div class="flex h-full w-full flex-col overflow-hidden">
            @if (!loading()) {
                <!--
                    Deferred blocks show optional UI. Empty @error blocks hide it
                    when its chunk fails to load, e.g. on a flaky mobile network.
                    Without them Angular raises an uncaught NG0750 error.
                -->
                @defer (on idle) {
                    <global-banner />
                } @error {}
                <div class="relative h-1/2 w-full flex-1">
                    <router-outlet />
                </div>
                @if (!simple()) {
                    @defer (on idle) {
                        <app-upload-list />
                    } @error {}
                }
            } @else {
                <div
                    loader
                    class="absolute inset-0 z-50 flex items-center justify-center"
                >
                    <div
                        class="border-base-300 absolute bottom-2 left-1/2 w-[24rem] -translate-x-1/2 overflow-hidden rounded-full border shadow"
                    >
                        <!-- Translations are not loaded yet, so the label is English -->
                        <mat-progress-bar
                            mode="indeterminate"
                            class="scale-150 rounded"
                            aria-label="Loading"
                        />
                    </div>
                </div>
            }
        </div>
        <global-loading />
        @if (!online() && !loading()) {
            <div
                role="alert"
                class="bg-error text-error-content fixed bottom-2 left-1/2 z-9999 -translate-x-1/2 rounded-3xl px-4 py-2 text-xs shadow-sm"
            >
                Unable to reach server... Some features may not work.
            </div>
        }
        @if (update_available() && !loading()) {
            <!-- Deferred so the tooltip and overlay code stay out of the initial bundle -->
            @defer {
                <section
                    role="status"
                    aria-live="polite"
                    class="border-info/30 bg-base-100 text-base-content fixed right-3 bottom-3 z-100 w-88 max-w-[calc(100vw-2rem)] rounded-md border p-3 shadow-lg"
                >
                    <div class="flex items-center gap-2">
                        <div class="min-w-0 flex-1">
                            <div class="text-sm font-medium">
                                Update available
                            </div>
                            <p class="mt-0.5 text-xs opacity-70">
                                Refresh to load the latest Backoffice version.
                            </p>
                        </div>
                        <button
                            icon
                            default
                            type="button"
                            (click)="refreshApplication()"
                            matTooltip="Refresh"
                            matTooltipPosition="left"
                        >
                            <icon>refresh</icon>
                        </button>
                    </div>
                </section>
            } @error {}
        }
    `,
    styles: [
        `
            [loader] {
                background-image: linear-gradient(
                    to right,
                    #c62828 0%,
                    #ef5350 100%
                );
            }
        `,
    ],
    imports: [
        GlobalBannerComponent,
        RouterOutlet,
        UploadListComponent,
        MatProgressBar,
        GlobalLoadingComponent,
        IconComponent,
        MatTooltip,
    ],
})
export class AppComponent implements OnInit {
    private _settings = inject(SettingsService);
    private _users = inject(BackofficeUsersService);
    private _cache = inject(SwUpdate);
    private _injector = inject(Injector);
    private _router = inject(Router);
    private _route = inject(ActivatedRoute);
    private _locale = inject(LocaleService, { optional: true });

    /** Whether the application is loading */
    public readonly loading = signal(false);
    /** Whether to hide the upload list, e.g. on MQTT routes */
    public readonly simple = signal(false);
    public readonly update_available = updateAvailable;

    private readonly _client_online = signalFromClient(onlineState());
    private readonly _browser_online = browserOnline();
    /** Whether PlaceOS is reachable. ts-client only flags auth failures, so also track the network. */
    public readonly online = computed(
        () => this._client_online() && this._browser_online(),
    );

    public refreshApplication() {
        location.reload();
    }

    public async ngOnInit() {
        setLoadingMessage('Initialising application...');
        this._route.queryParamMap.subscribe((params) => {
            if (params.has('lang')) {
                const locale = params.get('lang');
                this._locale?.setLocale(locale);
                localStorage.setItem('BACKOFFICE.locale', locale);
            }
        });
        // Load the snackbar lazily to keep the overlay code out of the initial bundle
        setNotifyOutlet(
            import('@angular/material/snack-bar').then(({ MatSnackBar }) =>
                this._injector.get(MatSnackBar),
            ),
        );
        setTranslationService(this._locale);
        this.loading.set(true);
        setLoadingMessage('Loading application settings...');
        /** Wait for settings to initialise */
        const settings_ready = await waitForSignalValue(
            this._settings.initialised,
            (_) => _,
        ).catch(() => false);
        if (!settings_ready) return this.onInitError();
        const settings = (this._settings.get('composer') ||
            {}) as PlaceSettings;
        settings.mock = !!this._settings.get('mock');
        settings.ignore_api_key = true;
        setLoadingMessage('Authenticating user...');
        /** Wait for authentication details to load */
        await setupPlace(settings).catch(() => this.onInitError());
        setupCache(this._cache);
        const user_ready = await waitForSignalValue(
            this._users.initialised,
            (_) => _,
            50,
            30 * 1000,
        ).catch(() => false);
        if (!user_ready) return this.onInitError();
        initSentry(this._injector, currentUser().id).catch((error) =>
            log('Init', 'Failed to start Sentry', [error], 'warn', true),
        );
        setLoadingMessage('Initialising locales...');
        // TranslatePipe is pure, so load translations before the shell renders
        await this._initLocale();
        this.loading.set(false);
        this._router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.simple.set(this._router.url.includes('mqtt'));
            }
        });
        setLoadingMessage('Checking staff tenants...');
        // Runs after the user loads, so currentUser() is set here
        this._checkTenants().catch((error) =>
            log('Init', 'Failed to check staff tenants', [error], 'warn'),
        );
    }

    private onInitError() {
        if (isMock()) return;

        log('Init', 'Failed to initialise user. Restarting application...');
        invalidateToken();
        location.reload();
    }

    /** Show one banner for staff tenants with expiring secrets */
    private async _checkTenants() {
        if (!currentUser()?.sys_admin) return;
        const [tenants, { tenantExpiryBanner }] = await Promise.all([
            get('/api/staff/v1/tenants').catch(() => []),
            import('./admin/staff-api.utilities'),
        ]);
        const banner = tenantExpiryBanner(
            Array.isArray(tenants) ? (tenants as PlaceTenant[]) : [],
        );
        if (banner) this._settings.post('banner', banner);
    }

    /**
     * Set the locale from the URL `lang` param, storage or the browser languages.
     * Resolves when the fallback and locale translations load, or after a timeout.
     */
    private async _initLocale() {
        let load: Promise<void> | undefined;
        try {
            // Router query params are not ready yet, so read lang from the URL
            const url_locale = localeFromUrl(location.search, location.hash);
            if (url_locale) {
                localStorage.setItem('BACKOFFICE.locale', url_locale);
            }
            let locale = localStorage.getItem('BACKOFFICE.locale');
            const locales = (this._settings.get('app.locales') as {
                id: string;
                name: string;
            }[]) || [{ id: 'en', name: 'English' }];
            if (locale) {
                load = this._locale?.setLocale(locale);
            } else {
                const list = navigator.languages || [];
                for (const lang of list) {
                    locale = locales.find((_) => _.id === lang)?.id;
                    if (!locale)
                        locale = locales.find((_) => lang.includes(_.id))?.id;
                    // Load the full browser tag (e.g. en-US), not the matched id,
                    // as locale files use full tags.
                    if (locale) {
                        load = this._locale?.setLocale(lang);
                        localStorage.setItem('BACKOFFICE.locale', lang);
                        break;
                    }
                }
            }
        } catch {
            // Ignore locale parsing errors
        }
        // Do not block start up on a slow or failed locale file
        await Promise.race([
            Promise.all([this._locale?.ready, load]).catch(() => undefined),
            new Promise((resolve) => setTimeout(resolve, LOCALE_TIMEOUT_MS)),
        ]);
    }
}
